import { baseResponseSchema } from 'services/api/api.types';
import { z } from 'zod/v4';
/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

enum GamblingLimitationTypeEnum {
  NLD = 'NLD',
  NLW = 'NLW',
  NLM = 'NLM',
  TLW = 'TLW',
}

const lossSchema = z
  .object({
    lossCounter: z.number(),
    availableLoss: z.number(),
    lossLimit: z.number(),
  })
  .optional();

const gamblingLimitationDetailsResponseSchema = baseResponseSchema.extend({
  responseObject: z.object({
    currentLimitationList: z.array(
      z
        .object({
          code: z.string(),
          description: z.string(),
          validityTimestamp: z.number(),
          endTimestamp: z.number().optional(),
          lastLimitUpdateTimestamp: z.number(),
          loss: lossSchema,
          time: z
            .object({
              availableTime: z.number(),
              timeCounter: z.number(),
              timeLimit: z.number(),
              nextAvailableTimestamp: z.number().optional(),
            })
            .optional(),
        })
        .optional(),
    ),
    nextLimitationList: z.array(
      z
        .object({
          code: z.string(),
          limit: z.number(),
          description: z.string(),
          validityTimestamp: z.number(),
        })
        .optional(),
    ),
  }),
});

type GamblingLimitationDetailsIResponse = z.infer<
  typeof gamblingLimitationDetailsResponseSchema
>;

type CurrentLimitationList =
  GamblingLimitationDetailsIResponse['responseObject']['currentLimitationList'];
type NextLimitationList =
  GamblingLimitationDetailsIResponse['responseObject']['nextLimitationList'];

type NormalizedCurrentLimitationListType = Record<
  GamblingLimitationTypeEnum,
  GamblingLimitationDetailsIResponse['responseObject']['currentLimitationList'][number]
>;

type NormalizedNextLimitationListType = Record<
  GamblingLimitationTypeEnum,
  GamblingLimitationDetailsIResponse['responseObject']['nextLimitationList'][number]
>;

type TransformedGamblingLimitationDetailsResponse = ReturnType<
  typeof transformGamblingLimitationDetailsResponse
>;

/*-------------------------------------------------------------------------------------------------
 * helpers
 * -----------------------------------------------------------------------------------------------*/

const getBettingLimitReachedLabel = (
  normalizedCurrentLimitationList: NormalizedCurrentLimitationListType,
) => {
  let betingLimitReachedLabel: null | string = null;

  if (normalizedCurrentLimitationList?.NLM?.loss?.availableLoss === 0)
    betingLimitReachedLabel = 'label.bettingLimit.monthly.reached';

  if (normalizedCurrentLimitationList?.NLW?.loss?.availableLoss === 0)
    betingLimitReachedLabel = 'label.bettingLimit.weekly.reached';

  if (normalizedCurrentLimitationList?.NLD?.loss?.availableLoss === 0)
    betingLimitReachedLabel = 'label.bettingLimit.daily.reached';

  return betingLimitReachedLabel;
};

const computeLastLimitUpdateDate = (
  normalizedCurrentLimitationList: NormalizedCurrentLimitationListType,
) => {
  return Math.max(
    ...Object.values(normalizedCurrentLimitationList).map(
      (limitation) => limitation?.lastLimitUpdateTimestamp ?? 0,
    ),
  );
};

const computeNextLimitUpdateDate = (
  normalizedNextLimitationList: NormalizedNextLimitationListType,
) => {
  return (
    Object.values(normalizedNextLimitationList).find(
      (limitation) =>
        limitation?.code === 'NLW' && limitation?.validityTimestamp,
    )?.validityTimestamp ?? null
  );
};

// if there is a currentLimitation with an endTimestamp but there is no corresponding nextLimitation with the same code, it means that the limit has been requested to be removed
// if there is a correspondi nextLimitation it was an update of the limit
const computeHasBettingLimitsBeenRemoved = (
  normalizedCurrentLimitationList: NormalizedCurrentLimitationListType,
  normalizedNextLimitationList: NormalizedNextLimitationListType,
) => {
  const currentLimitationWithEndTimestamp = Object.values(
    normalizedCurrentLimitationList,
  ).find((limitation) => limitation?.endTimestamp);

  if (!currentLimitationWithEndTimestamp)
    return { hasBettingLimitsBeenRemoved: false };

  const correspondingNextLimitation =
    normalizedNextLimitationList[currentLimitationWithEndTimestamp.code];

  if (!correspondingNextLimitation)
    return {
      hasBettingLimitsBeenRemoved: true,
      removalTimestamp: currentLimitationWithEndTimestamp.endTimestamp,
    };

  return { hasBettingLimitsBeenRemoved: false };
};
/*-------------------------------------------------------------------------------------------------
 * transformer helper
 * -----------------------------------------------------------------------------------------------*/
const transformGamblingLimitationDetailsResponse = (
  response: GamblingLimitationDetailsIResponse,
) => {
  //we normalize the data to make it easier to do lookups based on the code of the limitation, instead of having to do find every time
  const normalizedCurrentLimitationList =
    response.responseObject.currentLimitationList.reduce(
      (acc, limitation) => {
        if (!limitation) return acc;
        acc[limitation.code] = limitation;
        return acc;
      },
      {} as Record<
        GamblingLimitationTypeEnum,
        (typeof response.responseObject.currentLimitationList)[number]
      >,
    );

  const normalizedNextLimitationList =
    response.responseObject.nextLimitationList.reduce(
      (acc, limitation) => {
        if (!limitation) return acc;
        acc[limitation.code] = limitation;
        return acc;
      },
      {} as Record<
        GamblingLimitationTypeEnum,
        (typeof response.responseObject.nextLimitationList)[number]
      >,
    );

  const betingLimitReachedLabel = getBettingLimitReachedLabel(
    normalizedCurrentLimitationList,
  );

  const latestCurrentLimitationUpdateDate = computeLastLimitUpdateDate(
    normalizedCurrentLimitationList,
  );

  const nextUpdateDate = computeNextLimitUpdateDate(
    normalizedNextLimitationList,
  );

  const { hasBettingLimitsBeenRemoved, removalTimestamp } =
    computeHasBettingLimitsBeenRemoved(
      normalizedCurrentLimitationList,
      normalizedNextLimitationList,
    );

  const showBettingChart =
    (response.responseObject?.currentLimitationList &&
      response.responseObject?.currentLimitationList.length > 0) ||
    hasBettingLimitsBeenRemoved;

  return {
    normalizedCurrentLimitationList,
    normalizedNextLimitationList,
    betingLimitReachedLabel,
    hasBettingLimitsBeenRemoved,
    removalTimestamp,
    latestCurrentLimitationUpdateDate,
    nextUpdateDate,
    showBettingChart,
  };
};

export {
  gamblingLimitationDetailsResponseSchema,
  transformGamblingLimitationDetailsResponse,
};
export type {
  CurrentLimitationList,
  GamblingLimitationDetailsIResponse,
  NextLimitationList,
  NormalizedCurrentLimitationListType,
  NormalizedNextLimitationListType,
  TransformedGamblingLimitationDetailsResponse,
};
